import { useState, useEffect, useCallback } from 'react';

// Nom de la base de données IndexedDB
const DB_NAME = 'adhd-planner-proofs';
const DB_VERSION = 1;
const STORE_NAME = 'proofs';

// Types de preuves
export const PROOF_TYPES = {
  PHOTO: 'photo',
  SCREENSHOT: 'screenshot',
  VOICE: 'voice',
  TEXT: 'text',
  TIMER: 'timer'
};

// Initialisation de la base de données IndexedDB
function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('taskId', 'taskId', { unique: false });
        store.createIndex('date', 'date', { unique: false });
        store.createIndex('category', 'category', { unique: false });
      }
    };
  });
}

// Compression d'image (réduit la taille pour le stockage)
async function compressImage(dataUrl, maxWidth = 800, quality = 0.7) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Redimensionner si nécessaire
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.src = dataUrl;
  });
}

// Hook principal pour les preuves
export function useTaskProofs() {
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [db, setDb] = useState(null);

  // Initialiser la base de données
  useEffect(() => {
    openDatabase()
      .then((database) => {
        setDb(database);
        loadAllProofs(database);
      })
      .catch((error) => {
        console.error('Erreur ouverture IndexedDB:', error);
        setLoading(false);
      });

    return () => {
      if (db) db.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger toutes les preuves
  const loadAllProofs = useCallback(async (database) => {
    try {
      const transaction = database.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        setProofs(request.result || []);
        setLoading(false);
      };

      request.onerror = () => {
        console.error('Erreur chargement preuves:', request.error);
        setLoading(false);
      };
    } catch (error) {
      console.error('Erreur transaction:', error);
      setLoading(false);
    }
  }, []);

  // Ajouter une preuve
  const addProof = useCallback(async (proofData) => {
    if (!db) return null;

    try {
      // Compresser l'image si c'est une photo/screenshot
      let data = proofData.data;
      if ((proofData.type === PROOF_TYPES.PHOTO || proofData.type === PROOF_TYPES.SCREENSHOT) && data) {
        data = await compressImage(data);
      }

      const proof = {
        ...proofData,
        data,
        id: `proof-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date().toISOString(),
        date: proofData.date || new Date().toISOString().split('T')[0]
      };

      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      return new Promise((resolve, reject) => {
        const request = store.add(proof);

        request.onsuccess = () => {
          setProofs((prev) => [...prev, proof]);
          resolve(proof);
        };

        request.onerror = () => {
          console.error('Erreur ajout preuve:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Erreur compression/ajout:', error);
      return null;
    }
  }, [db]);

  // Supprimer une preuve
  const deleteProof = useCallback(async (proofId) => {
    if (!db) return false;

    try {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      return new Promise((resolve) => {
        const request = store.delete(proofId);

        request.onsuccess = () => {
          setProofs((prev) => prev.filter((p) => p.id !== proofId));
          resolve(true);
        };

        request.onerror = () => {
          console.error('Erreur suppression:', request.error);
          resolve(false);
        };
      });
    } catch (error) {
      console.error('Erreur suppression:', error);
      return false;
    }
  }, [db]);

  // Obtenir les preuves pour une tâche
  const getProofsForTask = useCallback((taskId) => {
    return proofs.filter((p) => p.taskId === taskId);
  }, [proofs]);

  // Obtenir les preuves pour une date
  const getProofsForDate = useCallback((date) => {
    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    return proofs.filter((p) => p.date === dateStr);
  }, [proofs]);

  // Obtenir les preuves par catégorie
  const getProofsByCategory = useCallback((category) => {
    return proofs.filter((p) => p.category === category);
  }, [proofs]);

  // Statistiques des preuves
  const stats = {
    total: proofs.length,
    photos: proofs.filter((p) => p.type === PROOF_TYPES.PHOTO).length,
    screenshots: proofs.filter((p) => p.type === PROOF_TYPES.SCREENSHOT).length,
    voice: proofs.filter((p) => p.type === PROOF_TYPES.VOICE).length,
    text: proofs.filter((p) => p.type === PROOF_TYPES.TEXT).length,
    timer: proofs.filter((p) => p.type === PROOF_TYPES.TIMER).length
  };

  return {
    proofs,
    loading,
    addProof,
    deleteProof,
    getProofsForTask,
    getProofsForDate,
    getProofsByCategory,
    stats,
    PROOF_TYPES
  };
}

// Utilitaire pour capturer une photo depuis la caméra
export async function capturePhoto() {
  return new Promise((resolve, reject) => {
    // Créer un input file temporaire
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Caméra arrière sur mobile

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = () => reject(new Error('Erreur lecture fichier'));
        reader.readAsDataURL(file);
      } else {
        reject(new Error('Aucun fichier sélectionné'));
      }
    };

    input.click();
  });
}

// Utilitaire pour sélectionner une image de la galerie
export async function selectImage() {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = () => reject(new Error('Erreur lecture fichier'));
        reader.readAsDataURL(file);
      } else {
        reject(new Error('Aucun fichier sélectionné'));
      }
    };

    input.click();
  });
}

// Utilitaire pour enregistrer une note vocale
export async function recordVoiceNote(maxDuration = 30000) {
  return new Promise(async (resolve, reject) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const chunks = [];

      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = () => reject(new Error('Erreur lecture audio'));
        reader.readAsDataURL(blob);

        // Arrêter le stream
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();

      // Arrêt automatique après maxDuration
      setTimeout(() => {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.stop();
        }
      }, maxDuration);

      // Retourner une fonction pour arrêter manuellement
      resolve({
        stop: () => mediaRecorder.stop(),
        recorder: mediaRecorder
      });
    } catch (error) {
      reject(error);
    }
  });
}

export default useTaskProofs;
